import { useQuery } from "@tanstack/react-query";
import {
  Users,
  CreditCard,
  AlertTriangle,
  TrendingUp,
  UserCheck,
  UserX,
  UserRound,
  UserRoundX,
  LogOut
} from "lucide-react";
import { getClients, getPayments, getConversations, getDashboardResumo, getClient } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useFotoPerfil, AvatarFoto } from "../services/fotoPerfil";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";

function normalizarRole(role) {
  return String(role || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function UserMenu({ user, logout }) {
  const [aberto, setAberto] = useState(false);
  const foto = useFotoPerfil(user?.id);

  return (
    <div className="user-menu-wrapper">
      <button
        className="user-menu-trigger"
        type="button"
        onClick={() => setAberto((valor) => !valor)}
      >
        <div className="user-avatar-sm">
          <AvatarFoto foto={foto} fallback={user?.full_name?.charAt(0)?.toUpperCase() || "U"} />
        </div>
        <span className="user-menu-name">{user?.full_name || "Usuário"}</span>
        <span className="user-menu-chevron">▾</span>
      </button>

      {aberto && (
        <div className="user-dropdown">
          <div className="user-dropdown-info">
            <strong>{user?.full_name || "Usuário"}</strong>
            <span>{user?.roles?.[0] || "Usuário"}</span>
          </div>
          <button type="button" className="user-dropdown-logout" onClick={logout}>
            <LogOut size={15} /> Sair
          </button>
        </div>
      )}
    </div>
  );
}

function formatarData(valor) {
  if (!valor) return "—";

  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return String(valor);

  return data.toLocaleDateString("pt-BR");
}

function CartaoResumo({ titulo, valor, classe, icone: Icon }) {
  return (
    <article className={`dashboard-resumo-card ${classe}`}>
      <div>
        <span className="dashboard-resumo-label">{titulo}</span>
        <strong>{Number(valor || 0).toLocaleString("pt-BR")}</strong>
      </div>
      <span className="dashboard-resumo-icon">
        <Icon size={22} />
      </span>
    </article>
  );
}

function CartaoEtapa({ titulo, valor, classe, icone: Icon, onDetalhes }) {
  return (
    <article className={`dashboard-etapa-card ${classe}`}>
      <div className="dashboard-etapa-topo">
        <span className="dashboard-etapa-icone">
          <Icon size={17} />
        </span>
        <strong>{titulo}</strong>
        <b>{Number(valor || 0).toLocaleString("pt-BR")}</b>
      </div>

      <button
        type="button"
        className="dashboard-etapa-detalhes"
        onClick={onDetalhes}
      >
        <span>Detalhes</span>
        <span>›</span>
      </button>
    </article>
  );
}

function DashboardAdministrativo({ user, logout, resumo }) {
  const navigate = useNavigate();

  const abrirFiltroClientes = (filtro) => {
    navigate(`/meus-clientes/consulta?dashboardFilter=${encodeURIComponent(filtro)}`);
  };

  const abrirEdicaoCliente = async (cliente) => {
    try {
      const clienteCompleto = await getClient(cliente.id);

      navigate("/clients", {
        state: {
          client: {
            ...clienteCompleto,
            _source: "cpfl"
          },
          openEdit: true
        }
      });
    } catch (erro) {
      console.error("Erro ao abrir cliente:", erro);
      alert("Não foi possível carregar os dados completos do cliente.");
    }
  };

  const cards = resumo?.cards || {};
  const ultimosCadastros = Array.isArray(resumo?.ultimosCadastros)
    ? resumo.ultimosCadastros
    : [];
  const dependentesMaiores21 = Array.isArray(resumo?.dependentesMaiores21)
    ? resumo.dependentesMaiores21
    : [];

  return (
    <div className="dashboard dashboard-administrativo">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p className="dashboard-subtitle">Visão geral do sistema</p>
        </div>
        <UserMenu user={user} logout={logout} />
      </div>

      <div className="dashboard-admin-breadcrumb">Dashboard</div>

      <div className="dashboard-etapas-grid">
        <CartaoEtapa
          titulo="Ativos"
          valor={cards.clientesAtivos}
          classe="etapa-verde"
          icone={UserCheck}
          onDetalhes={() => abrirFiltroClientes("ativos")}
        />
        <CartaoEtapa
          titulo="Inativos"
          valor={cards.clientesInativos}
          classe="etapa-vermelha"
          icone={UserX}
          onDetalhes={() => abrirFiltroClientes("inativos")}
        />
        <CartaoEtapa
          titulo="Dependentes"
          valor={cards.dependentes}
          classe="etapa-azul"
          icone={Users}
          onDetalhes={() => abrirFiltroClientes("dependentes")}
        />
        <CartaoEtapa
          titulo="Clientes Sem Dependentes"
          valor={cards.clientesSemDependentes}
          classe="etapa-amarela"
          icone={UserRoundX}
          onDetalhes={() => abrirFiltroClientes("sem_dependentes")}
        />
      </div>

      <div className="dashboard-admin-tabelas">
        <section className="dashboard-admin-tabela">
          <header>
            <h2><Users size={16} /> Últimos cadastros</h2>
          </header>

          <div className="dashboard-tabela-scroll">
            <table>
              <thead>
                <tr>
                  <th># Identificação</th>
                  <th>Nome</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {ultimosCadastros.length ? ultimosCadastros.map((cliente) => (
                  <tr key={cliente.id}>
                    <td>
                      <button
                        type="button"
                        className="dashboard-identificacao-link"
                        onClick={() => abrirEdicaoCliente(cliente)}
                      >
                        {cliente.identificacao || "—"}
                      </button>
                    </td>
                    <td>{cliente.nome || "—"}</td>
                    <td>{formatarData(cliente.createdDate)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="3" className="dashboard-tabela-vazia">
                      Nenhum cadastro encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="dashboard-admin-tabela">
          <header>
            <h2><UserRound size={16} /> Filhos dependentes que passaram dos 21 anos</h2>
          </header>

          <div className="dashboard-tabela-scroll">
            <table>
              <thead>
                <tr>
                  <th># Identificação</th>
                  <th>Nome</th>
                  <th>Idade</th>
                </tr>
              </thead>
              <tbody>
                {dependentesMaiores21.length ? dependentesMaiores21.map((dependente, index) => (
                  <tr key={`${dependente.clienteId}-${index}`}>
                    <td>{dependente.identificacao || "—"}</td>
                    <td>{dependente.nome || "—"}</td>
                    <td>{dependente.idade ?? "—"}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="3" className="dashboard-tabela-vazia">
                      Nenhum dependente encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();

  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const isAdminGestor = roles.some((role) => {
    const normalizada = normalizarRole(role);
    return normalizada === "admin" || normalizada === "gestor";
  });

  const dashboardAtendimento = roles.some((role) =>
    ["atendente", "vendedor", "pos vendas", "marketing", "financeiro"].includes(normalizarRole(role))
  );

  const { data: resumo, isLoading: carregandoResumo, error: erroResumo } = useQuery({
    queryKey: ["dashboard-resumo-administrativo"],
    queryFn: getDashboardResumo,
    enabled: isAdminGestor,
    staleTime: 60000
  });

  const { data: clientsData = { data: [] } } = useQuery({
    queryKey: ["clients"],
    queryFn: () => getClients("-created_date", 1, 100),
    enabled: !isAdminGestor && !dashboardAtendimento
  });

  const { data: payments = [] } = useQuery({
    queryKey: ["payments"],
    queryFn: () => getPayments("-created_date", 50),
    enabled: !isAdminGestor && !dashboardAtendimento
  });

  const { data: conversations = [] } = useQuery({
    queryKey: ["conversations"],
    queryFn: () => getConversations({ status: "aberto" }, "-created_date", 10),
    enabled: !isAdminGestor && !dashboardAtendimento
  });

  if (isAdminGestor) {
    if (carregandoResumo) {
      return <div className="dashboard dashboard-loading">Carregando Dashboard…</div>;
    }

    if (erroResumo) {
      return (
        <div className="dashboard dashboard-error">
          Não foi possível carregar o Dashboard administrativo.
        </div>
      );
    }

    return (
      <DashboardAdministrativo
        user={user}
        logout={logout}
        resumo={resumo}
      />
    );
  }

  if (dashboardAtendimento) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <div>
            <h1>Dashboard</h1>
            <p className="dashboard-subtitle">Visão geral do sistema</p>
          </div>
          <UserMenu user={user} logout={logout} />
        </div>

        <div className="welcome-card">
          <h2>👋 Olá, {user?.full_name || "Atendente"}!</h2>
          <p>
            Bem-vindo ao painel de atendimento. Aqui você pode consultar clientes,
            visualizar cadastros e gerenciar os atendimentos do dia.
          </p>
          <p className="welcome-hint">
            Utilize o menu lateral para acessar as funcionalidades.
          </p>
        </div>
      </div>
    );
  }

  const clients = clientsData.data || [];
  const totalClients = clients.length;
  const activeClients = clients.filter((cliente) => cliente.status === "ativo").length;
  const pendingPayments = payments.filter((pagamento) => pagamento.status === "pendente").length;
  const overduePayments = payments.filter((pagamento) => pagamento.status === "atrasado").length;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p className="dashboard-subtitle">Visão geral do sistema</p>
        </div>
        <UserMenu user={user} logout={logout} />
      </div>

      <div className="stats-grid">
        <StatCard title="Total Clientes" value={totalClients} icon={Users} color="blue" />
        <StatCard title="Clientes Ativos" value={activeClients} icon={TrendingUp} color="green" />
        <StatCard title="Pagamentos Pendentes" value={pendingPayments} icon={CreditCard} color="amber" />
        <StatCard title="Pagamentos Atrasados" value={overduePayments} icon={AlertTriangle} color="rose" />
      </div>

      <div className="dashboard-note">
        Visão resumida disponível para o seu perfil.
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  return (
    <div className="stat-card">
      <div className="stat-info">
        <h3>{title}</h3>
        <p className="stat-number">{value}</p>
      </div>
      <div className={`stat-icon ${color}`}>
        <Icon size={24} />
      </div>
    </div>
  );
}
